import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, CircularProgress, Alert, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, IconButton, Link,
  TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem,
  Pagination, Tooltip, CardContent, Divider, Button
} from '@mui/material';
import {
  Search, Download, RefreshCw, ArrowUp, ArrowDown, BarChart2, 
  Shield, Star, HardDrive, Filter, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { styled, alpha } from '@mui/material/styles';
import { 
  fadeInUp, GlassmorphicCard, SectionTitle, CompactCard, 
  SecondaryText, BodyText, StatusChipBase
} from '../styles/sharedStyles';

const StatCard = styled(GlassmorphicCard)(({ theme, color }) => ({
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '100px',
    height: '100px',
    background: `radial-gradient(circle at center, ${alpha(color || theme.palette.primary.main, 0.15)} 0%, transparent 70%)`,
    transform: 'translate(30%, -30%)',
    zIndex: 0,
  }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.02)',
  borderRadius: '20px',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  backdropFilter: 'blur(10px)',
  '& .MuiTableCell-root': {
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    padding: '16px',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    color: 'rgba(255, 255, 255, 0.6)',
    fontWeight: 600,
    fontSize: '0.85rem',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  }
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(0, 212, 255, 0.1)',
  color: '#00d4ff',
  border: '1px solid rgba(0, 212, 255, 0.2)',
  fontWeight: 600,
  fontSize: '0.75rem',
  height: 24,
}));

const CATEGORY_MAP = {
  0: '0-Day',
  1: 'Music Video',
  2: 'Apps',
  3: 'Consoles',
  4: 'E-book',
  5: 'Movies HD',
  6: 'Movies SD',
  7: 'Music',
  8: 'Movies',
  9: 'PC Games',
  10: 'TV HD',
  11: 'TV SD',
  12: 'XXX',
  13: 'TV UHD',
  14: 'Audiobook',
};

const getCategoryName = (id) => CATEGORY_MAP[id] || `Cat ${id}`;

const PolishTracker = ({ apiBaseUrl, token }) => {
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const [torrents, setTorrents] = useState([]);
  const [meta, setMeta] = useState({ total: 0, last_page: 1 });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('added');
  const [sortOrder, setSortOrder] = useState('desc');

  const formatBytes = (bytes) => {
    if (bytes === 0 || !bytes) return '0 B';
    const isNegative = bytes < 0;
    const absBytes = Math.abs(bytes);
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.floor(Math.log(absBytes) / Math.log(k));
    const val = parseFloat((absBytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    return isNegative ? `-${val}` : val;
  };

  const renderChange = (value, type = 'bytes') => {
    if (!value || value === 0) return null;
    const isPositive = value > 0;
    const color = isPositive ? '#00ff88' : '#ff4444';
    const prefix = isPositive ? '+' : '';
    
    let displayValue;
    if (type === 'bytes') {
      displayValue = formatBytes(value);
    } else if (type === 'ratio') {
      displayValue = value.toFixed(3);
    } else {
      displayValue = Math.floor(value).toLocaleString();
    }

    return (
      <Typography variant="caption" sx={{ color, fontWeight: 700, mt: 0.5, display: 'block' }}>
        {prefix}{displayValue} (24h)
      </Typography>
    );
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${apiBaseUrl}/api/polish-tracker/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.error) {
        setError(res.data.error);
      } else {
        setStats(res.data.stats);
        setProfile(res.data.profile);
      }
    } catch (err) {
      console.error('Error fetching PT stats:', err);
      setError('Failed to fetch stats from Polish Tracker');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token]);

  const searchTorrents = useCallback(async (pageNum = 1) => {
    try {
      setSearchLoading(true);
      const params = {
        page: pageNum,
        search: searchTerm || undefined,
        category: category !== 'all' ? category : undefined
      };
      
      const res = await axios.get(`${apiBaseUrl}/api/polish-tracker/torrents`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.error) {
        setTorrents([]);
      } else {
        setTorrents(res.data.data || []);
        setMeta(res.data.meta || { total: 0, last_page: 1 });
      }
    } catch (err) {
      console.error('Error searching PT torrents:', err);
    } finally {
      setSearchLoading(false);
    }
  }, [apiBaseUrl, token, searchTerm, category]);

  useEffect(() => {
    fetchData();
    searchTorrents(1);

    // Auto-refresh stats every 5 minutes (300,000 ms) to avoid frequent API calls
    const interval = setInterval(() => {
      fetchData();
    }, 300000);

    return () => clearInterval(interval);
  }, [fetchData, searchTorrents]); // Only fetch on mount and when fetchData changes

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const sortedTorrents = React.useMemo(() => {
    return [...torrents].sort((a, b) => {
      let valA, valB;
      
      if (sortBy === 'added') {
        valA = new Date(a.added || 0).getTime();
        valB = new Date(b.added || 0).getTime();
      } else if (sortBy === 'seeders') {
        valA = parseInt(a.seeders) || 0;
        valB = parseInt(b.seeders) || 0;
      } else if (sortBy === 'leechers') {
        valA = parseInt(a.leechers) || 0;
        valB = parseInt(b.leechers) || 0;
      } else if (sortBy === 'size') {
        valA = parseInt(a.size) || 0;
        valB = parseInt(b.size) || 0;
      } else {
        return 0;
      }
      
      return sortOrder === 'asc' ? valA - valB : valB - valA;
    });
  }, [torrents, sortBy, sortOrder]);

  const handleDownload = async (torrentId, torrentName) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/api/polish-tracker/download/${torrentId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${torrentName || 'torrent'}.torrent`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error downloading torrent:', err);
      alert('Failed to download torrent file');
    }
  };

  if (loading && !stats) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#00d4ff' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ animation: `${fadeInUp} 0.6s ease-out` }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <SectionTitle>
          <BarChart2 size={24} style={{ color: '#00d4ff' }} />
          Polish Tracker Dashboard
        </SectionTitle>
        <IconButton 
          onClick={fetchData} 
          disabled={loading}
          sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#00d4ff', background: 'rgba(0,212,255,0.1)' } }}
        >
          <RefreshCw size={20} className={loading ? 'spin' : ''} />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 4, borderRadius: '16px', background: 'rgba(255, 152, 0, 0.1)', color: '#ff9800', border: '1px solid rgba(255, 152, 0, 0.2)' }}>
          {error === 'API key missing' ? 'Polish Tracker API key is not configured in .env' : error}
        </Alert>
      )}

      {stats && (
        <Grid container spacing={3} sx={{ mb: 6 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard color="#00ff88">
              <CardContent>
                <SecondaryText sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ArrowUp size={16} color="#00ff88" />
                  UPLOADED
                </SecondaryText>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#fff' }}>
                  {formatBytes(stats.uploaded)}
                </Typography>
                {renderChange(stats.changes_24h?.uploaded, 'bytes')}
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', mt: 1, display: 'block' }}>
                  Lifetime upload volume
                </Typography>
              </CardContent>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard color="#ff4444">
              <CardContent>
                <SecondaryText sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ArrowDown size={16} color="#ff4444" />
                  DOWNLOADED
                </SecondaryText>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#fff' }}>
                  {formatBytes(stats.downloaded)}
                </Typography>
                {renderChange(stats.changes_24h?.downloaded, 'bytes')}
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', mt: 1, display: 'block' }}>
                  Lifetime download volume
                </Typography>
              </CardContent>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard color="#00d4ff">
              <CardContent>
                <SecondaryText sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <BarChart2 size={16} color="#00d4ff" />
                  RATIO
                </SecondaryText>
                <Typography variant="h4" sx={{ fontWeight: 800, color: stats.ratio >= 1 ? '#00ff88' : '#ff9800' }}>
                  {stats.ratio ? Math.floor(stats.ratio * 1000) / 1000 : '0.000'}
                </Typography>
                {renderChange(stats.changes_24h?.ratio, 'ratio')}
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', mt: 1, display: 'block' }}>
                  Share ratio
                </Typography>
              </CardContent>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard color="#ffd700">
              <CardContent>
                <SecondaryText sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Star size={16} color="#ffd700" />
                  POINTS
                </SecondaryText>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#ffd700' }}>
                  {stats.bonus_points ? Math.floor(stats.bonus_points).toLocaleString() : '0'}
                </Typography>
                {renderChange(stats.changes_24h?.bonus_points, 'points')}
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', mt: 1, display: 'block' }}>
                  Bonus points available
                </Typography>
              </CardContent>
            </StatCard>
          </Grid>
        </Grid>
      )}

      {profile && (
        <Grid container spacing={3} sx={{ mb: 6 }}>
          <Grid item xs={12} md={8}>
            <GlassmorphicCard sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff' }}>
                      {profile.username}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#00d4ff', fontWeight: 600 }}>
                      {profile.class || 'Member'}
                    </Typography>
                  </Box>
                  <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                    <StatusChipBase 
                      label={profile.status || 'Active'} 
                      sx={{ 
                        background: 'rgba(0, 255, 136, 0.1)', 
                        color: '#00ff88', 
                        border: '1px solid rgba(0, 255, 136, 0.3)' 
                      }} 
                    />
                  </Box>
                </Box>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', mb: 3 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4}>
                    <SecondaryText>JOINED</SecondaryText>
                    <Typography sx={{ color: '#fff', fontWeight: 500 }}>
                      {profile.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <SecondaryText>INVITES</SecondaryText>
                    <Typography sx={{ color: '#fff', fontWeight: 500 }}>
                      {profile.invites || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <SecondaryText>MAX SLOTS</SecondaryText>
                    <Typography sx={{ color: '#fff', fontWeight: 500 }}>
                      {profile.max_slots || 'Unlimited'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </GlassmorphicCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <GlassmorphicCard sx={{ height: '100%' }}>
              <CardContent>
                <SectionTitle sx={{ fontSize: '1.1rem', mb: 2 }}>
                  <Shield size={18} style={{ color: '#00d4ff' }} />
                  Account Security
                </SectionTitle>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <BodyText>2FA Status</BodyText>
                    <Chip 
                      label={profile.two_factor_enabled ? "Enabled" : "Disabled"} 
                      size="small"
                      sx={{ 
                        backgroundColor: profile.two_factor_enabled ? 'rgba(0, 255, 136, 0.1)' : 'rgba(255, 68, 68, 0.1)',
                        color: profile.two_factor_enabled ? '#00ff88' : '#ff4444'
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <BodyText>Passkey</BodyText>
                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>
                      {profile.passkey ? '••••••••' : 'Not set'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <BodyText>IRC Key</BodyText>
                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>
                      {profile.irc_key ? '••••••••' : 'Not set'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </GlassmorphicCard>
          </Grid>
        </Grid>
      )}

      {/* Torrent Search Section */}
      <Box sx={{ mb: 4 }}>
        <SectionTitle>
          <HardDrive size={24} style={{ color: '#00d4ff' }} />
          Search Torrents
        </SectionTitle>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 2, 
          p: 2, 
          borderRadius: '20px', 
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          mb: 3
        }}>
          <TextField
            fullWidth
            placeholder="Search Polish Tracker..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && searchTorrents(1)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} color="rgba(255,255,255,0.4)" />
                </InputAdornment>
              ),
            }}
            sx={{ 
              flex: 1, minWidth: '200px',
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
              }
            }}
          />
          <FormControl sx={{ minWidth: '150px' }}>
            <InputLabel sx={{ color: 'rgba(255,255,255,0.5)' }}>Category</InputLabel>
            <Select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              label="Category"
              sx={{ 
                color: '#fff',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
              }}
            >
              <MenuItem value="all">All Categories</MenuItem>
              <MenuItem value="5">Movies HD</MenuItem>
              <MenuItem value="6">Movies SD</MenuItem>
              <MenuItem value="10">TV HD</MenuItem>
              <MenuItem value="11">TV SD</MenuItem>
              <MenuItem value="13">TV UHD</MenuItem>
              <MenuItem value="9">PC Games</MenuItem>
              <MenuItem value="7">Music</MenuItem>
              <MenuItem value="2">Apps</MenuItem>
              <MenuItem value="4">E-Books</MenuItem>
              <MenuItem value="14">Audiobooks</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={() => searchTorrents(1)}
            disabled={searchLoading}
            startIcon={searchLoading ? <CircularProgress size={20} color="inherit" /> : <Search size={20} />}
            sx={{ 
              background: '#00d4ff', 
              color: '#000',
              fontWeight: 600,
              borderRadius: '12px',
              px: 3,
              '&:hover': { background: '#00aed1' },
              '&:disabled': { background: 'rgba(0,212,255,0.5)' }
            }}
          >
            Search
          </Button>
        </Box>

        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Torrent Name</TableCell>
                <TableCell align="center">Category</TableCell>
                <TableCell 
                  align="center" 
                  onClick={() => handleSort('size')}
                  sx={{ cursor: 'pointer', '&:hover': { color: '#00d4ff' } }}
                >
                  Size {sortBy === 'size' && (sortOrder === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                    <Box 
                      onClick={() => handleSort('seeders')}
                      sx={{ cursor: 'pointer', '&:hover': { color: '#00ff88' }, color: sortBy === 'seeders' ? '#00ff88' : 'inherit' }}
                    >
                      S {sortBy === 'seeders' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)' }}>/</Typography>
                    <Box 
                      onClick={() => handleSort('leechers')}
                      sx={{ cursor: 'pointer', '&:hover': { color: '#ff4444' }, color: sortBy === 'leechers' ? '#ff4444' : 'inherit' }}
                    >
                      L {sortBy === 'leechers' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell 
                  align="center"
                  onClick={() => handleSort('added')}
                  sx={{ cursor: 'pointer', '&:hover': { color: '#00d4ff' }, color: sortBy === 'added' ? '#00d4ff' : 'inherit' }}
                >
                  Added {sortBy === 'added' && (sortOrder === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedTorrents.length > 0 ? (
                sortedTorrents.map((torrent) => (
                  <TableRow key={torrent.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff', mb: 0.5 }}>
                          {torrent.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {torrent.freeleech && <Chip label="FREE" size="small" sx={{ height: 16, fontSize: '0.65rem', background: '#00ff88', color: '#000', fontWeight: 700 }} />}
                          {torrent.double_upload && <Chip label="2X" size="small" sx={{ height: 16, fontSize: '0.65rem', background: '#00d4ff', color: '#000', fontWeight: 700 }} />}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <CategoryChip label={getCategoryName(torrent.category)} />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        {formatBytes(torrent.size)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: '#00ff88', fontWeight: 600 }}>{torrent.seeders || 0}</Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)' }}>/</Typography>
                        <Typography variant="body2" sx={{ color: '#ff4444', fontWeight: 600 }}>{torrent.leechers || 0}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        {torrent.added ? new Date(torrent.added).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Tooltip title="Download .torrent">
                          <IconButton 
                            onClick={() => handleDownload(torrent.id, torrent.name)}
                            size="small" 
                            sx={{ color: '#00d4ff', background: 'rgba(0,212,255,0.1)', '&:hover': { background: 'rgba(0,212,255,0.2)' } }}
                          >
                            <Download size={18} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View on Polish Tracker">
                          <IconButton 
                            component={Link}
                            href={`https://api-test.pte.nu/torrent/${torrent.id}`}
                            target="_blank"
                            size="small" 
                            sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff' } }}
                          >
                            <ExternalLink size={18} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.3)' }}>
                      {searchLoading ? 'Searching...' : 'No torrents found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>

        {meta.last_page > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination 
              count={meta.last_page} 
              page={page} 
              onChange={(e, p) => {
                setPage(p);
                searchTorrents(p);
              }}
              sx={{
                '& .MuiPaginationItem-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(0, 212, 255, 0.2)',
                    color: '#00d4ff',
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                  },
                }
              }}
            />
          </Box>
        )}
      </Box>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </Box>
  );
};

export default PolishTracker;
